import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as tienda from '@/api/tienda.api';
import { SeccionDetalle } from '@/components/datos/DetailDrawer';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { useToast } from '@/hooks/useToast';

/**
 * CÓMO SALE ESTA PIEZA EN LA TIENDA WEB.
 *
 * Dos cosas que solo la dueña puede decir de una pieza: qué contarle a la
 * clienta (la descripción) y si, agotada, se puede traer por encargo.
 *
 * «Por encargo» nace apagado: es una promesa, y la tienda no promete por su
 * cuenta. Apagado, una pieza sin existencia dice «agotada».
 */
export function EnLaTiendaWeb({ producto }: { producto: tienda.Producto }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [descripcion, setDescripcion] = useState(producto.description ?? '');

  const guardar = useMutation({
    mutationFn: (cambios: Parameters<typeof tienda.actualizarProducto>[1]) =>
      tienda.actualizarProducto(producto.id, cambios),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['productos'] });
      void qc.invalidateQueries({ queryKey: ['tienda-web'] });
      toast.success('Guardado');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cambio = descripcion.trim() !== (producto.description ?? '');
  const sinFoto = (producto.imagesJson ?? []).length === 0;

  return (
    <SeccionDetalle titulo="En la tienda web">
      {sinFoto && (
        <p className="mb-2 text-sm text-amber-700 dark:text-amber-400">
          Sin foto no sale en la tienda. Súbele al menos una abajo.
        </p>
      )}
      <Textarea
        label="Descripción"
        rows={3}
        placeholder="Material, horma, cómo se lleva…"
        value={descripcion}
        onChange={(e) => setDescripcion(e.target.value)}
      />
      {cambio && (
        <Button
          size="sm"
          className="mt-2"
          loading={guardar.isPending}
          onClick={() => guardar.mutate({ description: descripcion.trim() })}
        >
          Guardar descripción
        </Button>
      )}
      <div className="mt-3 flex items-center gap-3">
        <Toggle
          checked={producto.aceptaEncargo}
          onChange={(v) => guardar.mutate({ aceptaEncargo: v })}
          disabled={guardar.isPending}
          label="Se puede encargar si se agota"
        />
        <div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
            Se puede encargar si se agota
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {producto.aceptaEncargo
              ? 'Agotada, la tienda la ofrece «por encargo».'
              : 'Agotada, la tienda dice «agotada».'}
          </p>
        </div>
      </div>
    </SeccionDetalle>
  );
}
